// Implements: F-SHR-009, F-SHR-006, F-SHR-008, F-UIX-007
/**
 * The person's links across mockups as a table (the redesign mockup's "Enlaces compartidos"). Pure
 * presentation.
 *
 * A row shows what the server lists (the mockup's name with the link's label under it, artifact,
 * mode, expiry, whether it is protected, the open count, the last open and the derived state) and
 * **never** the link itself: the URL is shown once, when the link is created (owner question 3), so
 * the table offers no copy. A frozen link names no version number, because the listing carries only
 * the version's id.
 *
 * "Revocar" and the edit entry of the row menu are offered only where the server's per-link
 * `may_revoke` says so, never on a revoked link: the client spells no role and guesses no permission.
 * A refusal still comes back as the server's sentence.
 *
 * Mockup v3 P8: an expired link offers no "Revocar" (owner decision) but keeps its edit entry and
 * the label pencil (`LinkLabel`): the server lets an expired link change its label and expiry. An
 * active row offers its eye, "Ver como visitante" (`onVisitor`), which never counts a visit.
 */

import type { ReactElement } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import type { IconName } from "../../../shared/design-system/icons";
import { Badge } from "../../../shared/ui/atoms/Badge";
import type { BadgeTone } from "../../../shared/ui/atoms/Badge";
import { Button, buttonClassName } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { dayText } from "../../../shared/ui/dayText";
import { Menu } from "../../../shared/ui/molecules/Menu";
import type { MenuEntry } from "../../../shared/ui/molecules/Menu";
import type { PanelLinkView } from "../../../types/sharing";
import { LinkLabel } from "./LinkLabel";
import { isEditable, isRevocable, linkName } from "./ShareLinkList";
import styles from "./LinksTable.module.css";

type StateKey =
  | "sharing.state.active"
  | "sharing.state.expired"
  | "sharing.state.revoked"
  | "sharing.state.suspended"
  | "sharing.state.unknown";

const STATES: Readonly<Record<string, { key: StateKey; tone: BadgeTone; icon: IconName }>> = {
  active: { key: "sharing.state.active", tone: "success", icon: "check-circle" },
  expired: { key: "sharing.state.expired", tone: "warning", icon: "history" },
  revoked: { key: "sharing.state.revoked", tone: "neutral", icon: "close" },
  suspended: { key: "sharing.state.suspended", tone: "warning", icon: "alert-triangle" },
};
const UNKNOWN_STATE = { key: "sharing.state.unknown", tone: "neutral", icon: "info" } as const;

export interface LinksTableProps {
  readonly links: readonly PanelLinkView[];
  /** The link whose revoke is in flight, if any. */
  readonly pendingId: string | null;
  readonly onRevoke: (link: PanelLinkView) => void;
  readonly onEdit: (link: PanelLinkView) => void;
  readonly onOpenMockup: (link: PanelLinkView) => void;
  /** Renames a link's label; answers the refusal's sentence, or `null` once it is accepted. */
  readonly onRename?: ((link: PanelLinkView, label: string) => Promise<string | null>) | undefined;
  /** Opens what a visitor of this link sees (offered on active links only). */
  readonly onVisitor?: ((link: PanelLinkView) => void) | undefined;
}

export function LinksTable({
  links,
  pendingId,
  onRevoke,
  onEdit,
  onOpenMockup,
  onRename,
  onVisitor,
}: LinksTableProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className={styles.wrap}>
      <table className={styles.table} aria-label={t("sharing.panel.title")}>
        <thead>
          <tr>
            <th scope="col">{t("sharing.col.mockup")}</th>
            <th scope="col">{t("sharing.col.artifact")}</th>
            <th scope="col">{t("sharing.col.mode")}</th>
            <th scope="col">{t("sharing.col.expires")}</th>
            <th scope="col">{t("sharing.col.protected")}</th>
            <th scope="col" className={styles.number}>
              {t("sharing.col.opens")}
            </th>
            <th scope="col">{t("sharing.col.lastOpened")}</th>
            <th scope="col">{t("sharing.col.state")}</th>
            <th scope="col" className={styles.actionsHead}>
              <span className={styles.srOnly}>{t("sharing.col.actions")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {links.map((link) => {
            const name = linkName(link, t("sharing.list.noLabel"));
            const editable = isEditable(link);
            const state = STATES[link.state] ?? UNKNOWN_STATE;
            const menu: readonly MenuEntry[] = [
              {
                kind: "item",
                id: "open",
                label: t("sharing.row.open"),
                icon: "external",
                onSelect: () => {
                  onOpenMockup(link);
                },
              },
              ...(editable
                ? ([
                    {
                      kind: "item",
                      id: "edit",
                      label: t("sharing.edit.menu"),
                      icon: "history",
                      onSelect: () => {
                        onEdit(link);
                      },
                    },
                  ] satisfies MenuEntry[])
                : []),
            ];
            return (
              <tr key={link.id}>
                <td>
                  <Link className={styles.mockup} to={`/lienzo/${encodeURIComponent(link.mockup_id)}`}>
                    {link.mockup_name}
                  </Link>
                  <div className={styles.meta}>
                    <LinkLabel
                      label={link.label}
                      name={name}
                      editable={editable}
                      onRename={onRename === undefined ? undefined : (label) => onRename(link, label)}
                    />
                  </div>
                </td>
                <td>{t(link.artifact === "diagram" ? "sharing.artifact.diagram" : "sharing.artifact.prototype")}</td>
                <td>{t(link.mode === "frozen" ? "sharing.panel.mode.frozen" : "sharing.panel.mode.live")}</td>
                <td className={styles.date}>
                  <time dateTime={link.expires_at}>{dayText(link.expires_at)}</time>
                </td>
                <td>
                  {link.has_password ? (
                    <Badge tone="outline">
                      <Icon name="lock" size="sm" />
                      {t("common.yes")}
                    </Badge>
                  ) : (
                    <span className={styles.meta}>{t("common.no")}</span>
                  )}
                </td>
                <td className={styles.number}>{link.total_opens}</td>
                <td className={styles.date}>
                  {link.last_opened_at === null ? (
                    t("sharing.panel.neverOpened")
                  ) : (
                    <time dateTime={link.last_opened_at}>{dayText(link.last_opened_at)}</time>
                  )}
                </td>
                <td>
                  <Badge tone={state.tone}>
                    <Icon name={state.icon} size="sm" />
                    {t(state.key)}
                  </Badge>
                </td>
                <td className={styles.actions}>
                  {onVisitor === undefined || link.state !== "active" ? null : (
                    <Button
                      variant="ghost"
                      iconOnly
                      label={t("sharing.visitor.row", { label: name })}
                      onClick={() => {
                        onVisitor(link);
                      }}
                    >
                      <Icon name="eye" />
                    </Button>
                  )}
                  {isRevocable(link) ? (
                    <Button
                      variant="ghost"
                      label={t("sharing.revoke.for", { name })}
                      disabled={pendingId !== null}
                      loading={pendingId === link.id}
                      onClick={() => {
                        onRevoke(link);
                      }}
                    >
                      {t("sharing.revoke")}
                    </Button>
                  ) : null}
                  <Menu
                    label={t("sharing.row.menu", { name })}
                    align="end"
                    entries={menu}
                    renderTrigger={(trigger) => (
                      <button
                        type="button"
                        {...trigger}
                        className={buttonClassName("ghost", "md", true)}
                        aria-label={t("sharing.row.menu", { name })}
                      >
                        <Icon name="more" />
                      </button>
                    )}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
